import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DocumentFolder, CreateDocumentFolderInput } from '@peles/shared';
import * as foldersApi from '../api/document-folders';

/**
 * `enabled` is what keeps a caller that has its own folder list from fetching
 * every `document_folders` row to ignore them — see `UploadLinkFields`, where a
 * conversation supplies the library's folders instead.
 */
export function useDocumentFolders(
  params: { parentId?: string | null; entityType?: string; entityId?: string } = {},
  enabled = true,
) {
  return useQuery<DocumentFolder[]>({
    queryKey: ['document-folders', params],
    queryFn: () => foldersApi.listDocumentFolders(params),
    enabled,
  });
}

export function useRootFolders() {
  return useQuery<DocumentFolder[]>({
    queryKey: ['document-folders', { parentId: null }],
    queryFn: () => foldersApi.listDocumentFolders({ parentId: null }),
  });
}

export function useFolderBreadcrumb(id: string | null) {
  return useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['document-folders-breadcrumb', id],
    queryFn: () => foldersApi.getFolderBreadcrumb(id!),
    enabled: !!id,
  });
}

export function useCreateDocumentFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentFolderInput) => foldersApi.createDocumentFolder(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
    },
  });
}

export function useUpdateDocumentFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      foldersApi.updateDocumentFolder(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
    },
  });
}

export function useDeleteDocumentFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => foldersApi.deleteDocumentFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      queryClient.invalidateQueries({ queryKey: ['entity-attachments'] });
      queryClient.invalidateQueries({ queryKey: ['entity-attachments-folder'] });
    },
  });
}

export function useEnsureEntityFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      entityName,
    }: {
      entityType: string;
      entityId: string;
      entityName: string;
    }) => foldersApi.ensureEntityFolder(entityType, entityId, entityName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
    },
  });
}
